import { Map } from 'immutable';
import { createSelector } from 'reselect';
import {
    ContactFieldsSimple,
    ContactFieldsNumberMap,
    ContactFull,
} from 'flow/types/Contact';
import { ContactKey, ExtensionKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import {
    convertNumberToE164,
    getContactFromSourcesByNumber,
} from 'flow/utils/call-utils';
import { getContactFromSourcesByEmail } from 'flow/utils/contact-utils';
import { getSelectedCtiContactByNumber } from './cti-integration-selectors';

const selectLookupData = (state: State) =>
    state.contacts.get('cachedLookups', Map());

const selectExternalContacts = (state: State) =>
    state.contacts.get('externalContacts', Map());

export const selectExternalContactsExistsInStore = (state: State) =>
    !!state.contacts.get('externalContacts');

const selectFlowContacts = (state: State) =>
    state.contacts.get('flowContacts', Map());

export const selectContactByKey = (state: State, key: ContactKey) =>
    state.contacts.getIn(['externalContacts', key]) ||
    state.contacts.getIn(['flowContacts', key]);

export const selectSocialIntegrationContacts = (state: State) =>
    state.contacts.get('socialIntegrationContacts', Map());

const selectExtensions = (state: State) =>
    state.extensions.get('extensions', Map());

// NOTE: This is a single shared createSelector instance, so its memoization
// thrashes whenever two consumers call it with different (contactKey, extensionKey)
// tuples in the same render pass. Prefer makeSelectContact() below for new
// call sites; existing usages should be migrated in a follow-up PR.
export const getContact = createSelector(
    [
        selectFlowContacts,
        selectExternalContacts,
        selectSocialIntegrationContacts,
        selectExtensions,
        (_state: State, contactKey: ContactKey) => contactKey,
        (_state: State, _contactKey: ContactKey, extensionKey?: ExtensionKey) =>
            extensionKey,
    ],
    (
        flowContacts,
        externalContacts,
        socialIntegrationContacts,
        extensions,
        contactKey,
        extensionKey,
    ) => {
        const allContacts = Map().concat(
            flowContacts,
            externalContacts,
            socialIntegrationContacts,
        );

        // Multitenant users don't have permission to get contact data from the contact
        // api, api/internal/contacts/${contactEntityKey}, for users from other customers.
        // When this is the case there will be no data in the store under contact and we
        // get the contact from the extension instead.
        return (
            allContacts.get(contactKey) ||
            extensions.getIn([extensionKey, 'contact'])
        );
    },
);

// Factory variant of getContact — each consumer should create its own instance
// via useMemo(makeSelectContact, []) so memoization is per-component and won't
// thrash when used across multiple components mounted at the same time.
export const makeSelectContact = () =>
    createSelector(
        [
            selectFlowContacts,
            selectExternalContacts,
            selectSocialIntegrationContacts,
            selectExtensions,
            (_state: State, contactKey: ContactKey) => contactKey,
            (
                _state: State,
                _contactKey: ContactKey,
                extensionKey?: ExtensionKey,
            ) => extensionKey,
        ],
        (
            flowContacts,
            externalContacts,
            socialIntegrationContacts,
            extensions,
            contactKey,
            extensionKey,
        ) => {
            const allContacts = flowContacts
                .concat(externalContacts)
                .concat(socialIntegrationContacts);

            return (
                allContacts.get(contactKey) ||
                (extensions.getIn([extensionKey, 'contact']) as
                    | TypedMap<ContactFull>
                    | undefined)
            );
        },
    );

export const getContactKeyFromExtensionKey = (
    state: State,
    extensionKey: ExtensionKey,
) => state.extensions.getIn(['extensions', extensionKey, 'contact', 'key']);

const getExtensionsContacts = createSelector([selectExtensions], (extensions) =>
    extensions.map((ext) => ext.get('contact')),
);

export const selectAllContacts = createSelector(
    [
        getExtensionsContacts,
        selectExternalContacts,
        selectSocialIntegrationContacts,
        selectFlowContacts,
    ],
    (
        extensionContacts,
        externalContacts,
        socialIntegrationContacts,
        flowContacts,
    ) =>
        extensionContacts
            .toList()
            .concat(externalContacts.toList())
            .concat(socialIntegrationContacts.toList())
            .concat(flowContacts.toList()),
);

// This is a function creating a memoized selector, which means that you need to create you own instance of it in a makeMapStateToProps.
// Read more here: https://redux.js.org/recipes/computing-derived-data#sharing-selectors-across-multiple-components
export const makeGetContactDataForNumber = () =>
    createSelector(
        [
            selectAllContacts,
            selectLookupData,
            getSelectedCtiContactByNumber,
            (_state: State, number: string) => number,
        ],
        (contacts, lookupData, selectedCtiContact, number) =>
            getContactFromSourcesByNumber(
                contacts,
                lookupData,
                selectedCtiContact,
                number,
            ),
    );

export const getContactDataForNumber = makeGetContactDataForNumber();

export const getCoListeningContact = (
    state: State,
    number: string,
    contact: TypedMap<ContactFull>,
    ownCountryCode: string,
) => {
    if (contact) {
        return contact;
    }
    const e164SubjectNumber = convertNumberToE164(number, ownCountryCode);
    return getContactDataForNumber(state, e164SubjectNumber);
};

// Selector below should be used with useMemo so that a unique instance of it is
// created, or memoization will break if used in multiple components mounted and
// visible at the same time.
// To see how it is used, search for usages of makeSelectContactDataForNumber and mimic that.
export const makeSelectContactDataForNumber = () =>
    createSelector(
        [
            selectAllContacts,
            selectLookupData,
            getSelectedCtiContactByNumber,
            (_state: State, number: string) => number,
        ],
        (contacts, lookupData, selectedCtiContact, number) =>
            getContactFromSourcesByNumber(
                contacts,
                lookupData,
                selectedCtiContact,
                number,
            ),
    );

export const makeSelectContactDataForEmail = () =>
    createSelector(
        [selectAllContacts, (_state: State, email: string) => email],
        (contacts, email) => getContactFromSourcesByEmail(contacts, email),
    );

export const selectEditableContactDTO = (state: State, key: ContactKey) =>
    state.contacts.getIn(['flowContacts', key, 'editableContactDTO']) ||
    state.contacts.getIn(['externalContacts', key, 'editableContactDTO']);

export const selectEditableContactFields = (state: State, key: ContactKey) =>
    selectEditableContactDTO(state, key)?.get('contactFields');

export const selectEditableContactValueSimple = (
    state: State,
    key: ContactKey,
    value: keyof ContactFieldsSimple,
) => selectEditableContactFields(state, key)?.getIn([value, 'value']);

export const selectEditableContactNumberDto = (
    state: State,
    key: ContactKey,
    value: keyof ContactFieldsNumberMap,
) => selectEditableContactFields(state, key)?.getIn([value, 'number']);

export const selectHasEditableContactFields = (state: State, key: ContactKey) =>
    !!selectEditableContactFields(state, key);

export const selectEditableContactValueIsEditable = (
    state: State,
    key: ContactKey,
    value: keyof ContactFieldsSimple | keyof ContactFieldsNumberMap,
): boolean | undefined =>
    selectEditableContactFields(state, key)?.getIn([value, 'editable']);
